/**
 * Social sign-in in its own window (Figma 09 · Social accounts / 13 "Finish
 * signing in on Facebook. This page waits for you.").
 *
 * The provider's callback (backend) always redirects to
 * /dashboard/settings?page=connections&connected=…|socialError=…, so the popup
 * lands on Settings. It recognises itself by a sessionStorage flag (copied
 * from the opener when the window is made — window.opener and window.name do
 * not survive the provider's cross-origin pages), hands the result over a
 * BroadcastChannel and closes. If the browser will not let it close, it simply
 * shows Settings with the same result. A blocked popup falls back to the old
 * full-page redirect.
 */
import { useEffect, useState } from "react";

const CHANNEL = "co-social-oauth";
const POPUP_FLAG = "co-social-oauth-popup";
const RESULT_KEYS = ["connected", "moved", "socialError"] as const;

export type SocialOAuthResult = Partial<Record<(typeof RESULT_KEYS)[number], string>>;

function readResult(search: string): SocialOAuthResult | null {
  const params = new URLSearchParams(search);
  const result: SocialOAuthResult = {};
  for (const key of RESULT_KEYS) {
    const value = params.get(key);
    if (value) result[key] = value;
  }
  return Object.keys(result).length > 0 ? result : null;
}

function safeSession<T>(fn: (s: Storage) => T): T | null {
  try {
    return fn(window.sessionStorage);
  } catch {
    return null;
  }
}

/** Opens the provider in a window; returns false when it fell back to a redirect. */
export function openSocialOAuth(authUrl: string): boolean {
  safeSession((s) => s.setItem(POPUP_FLAG, "1"));
  const width = 600;
  const height = 760;
  const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
  const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);
  let popup: Window | null = null;
  try {
    popup = window.open(authUrl, CHANNEL, `popup,width=${width},height=${height},left=${left},top=${top}`);
  } catch {
    popup = null;
  }
  // The popup has its own copy of the flag now; this tab must not keep it.
  safeSession((s) => s.removeItem(POPUP_FLAG));
  if (!popup) {
    window.location.href = authUrl;
    return false;
  }
  return true;
}

/** In the opener: calls back once the popup reports a result. */
export function useSocialOAuthResult(onResult: (result: SocialOAuthResult) => void) {
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const channel = new BroadcastChannel(CHANNEL);
    channel.onmessage = (event: MessageEvent<SocialOAuthResult>) => onResult(event.data);
    return () => channel.close();
  }, [onResult]);
}

/** In the popup: report the result and close. True while this tab is that popup. */
export function useSocialOAuthPopupReturn(): boolean {
  const [isPopup] = useState(() => {
    const flagged = safeSession((s) => s.getItem(POPUP_FLAG) === "1") ?? false;
    return flagged && readResult(window.location.search) != null;
  });

  useEffect(() => {
    if (!isPopup) return;
    const result = readResult(window.location.search);
    safeSession((s) => s.removeItem(POPUP_FLAG));
    try {
      const channel = new BroadcastChannel(CHANNEL);
      channel.postMessage(result);
      channel.close();
    } catch {
      // No channel: the opener still sees the account on its next refetch.
    }
    window.close();
    // Still open (the browser refused): show Settings with the result instead.
    const fallback = window.setTimeout(() => window.location.replace(window.location.href), 400);
    return () => window.clearTimeout(fallback);
  }, [isPopup]);

  return isPopup;
}
